import { useState } from 'react'
import { Clapperboard, Play } from 'lucide-react'

import type { ShopGuideVideo } from '@/lib/api/shop-guide-videos-api'
import { GuideVideoPlayer } from '@/components/retail/guide-videos/guide-video-player'
import {
    formatGuideDuration,
    groupGuideVideos,
} from '@/components/retail/guide-videos/guide-video-utils'
import { useShopGuideVideos } from '@/hooks/use-shop-guide-videos-query'
import { thumbOf } from '@/lib/api/shop-catalog-api'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/**
 * Tab "Xem video": video đang hiện, gom theo nhóm. Thẻ chỉ có ảnh bìa (thumb
 * nhỏ, lazy) — chưa bấm thì không tải byte video nào.
 */
export function GuideVideoGallery() {
    const query = useShopGuideVideos()
    const [playing, setPlaying] = useState<ShopGuideVideo | null>(null)

    if (query.isLoading) {
        return (
            <p className="text-sm text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (query.isError) {
        return (
            <p className="text-sm text-destructive" role="alert">
                {m.guideVideo_load_failed()}
            </p>
        )
    }
    const groups = groupGuideVideos(query.data?.data ?? [])
    if (groups.length === 0) {
        return (
            <p className="py-8 text-center text-sm text-muted-foreground">
                {m.guideVideo_empty()}
            </p>
        )
    }

    return (
        <div className="space-y-8">
            {groups.map((g) => (
                <section key={g.name} aria-label={g.name} className="space-y-3">
                    <div className="flex items-baseline gap-2">
                        <h2 className="text-base font-semibold">{g.name}</h2>
                        <span className="text-xs text-muted-foreground">
                            {m.guideVideo_count({ count: g.items.length })}
                        </span>
                    </div>
                    <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {g.items.map((v) => (
                            <li key={v.id}>
                                <GuideVideoCard
                                    video={v}
                                    onPlay={() => setPlaying(v)}
                                />
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
            <GuideVideoPlayer
                video={playing}
                onClose={() => setPlaying(null)}
            />
        </div>
    )
}

function GuideVideoCard({
    video,
    onPlay,
}: {
    video: ShopGuideVideo
    onPlay: () => void
}) {
    const duration = formatGuideDuration(video.duration_seconds)
    return (
        <button
            type="button"
            onClick={onPlay}
            aria-label={m.guideVideo_play_aria({ title: video.title })}
            className={twJoin(
                'group flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-lg border bg-card text-left',
                'transition-colors hover:border-primary focus-visible:border-primary',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
        >
            <span className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-muted">
                {video.poster_url ? (
                    <img
                        src={thumbOf(video.poster_url)}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 size-full object-contain"
                    />
                ) : (
                    <Clapperboard
                        aria-hidden="true"
                        className="size-10 text-muted-foreground"
                    />
                )}
                <span
                    aria-hidden="true"
                    className={twJoin(
                        'absolute inset-0 flex items-center justify-center bg-black/10',
                        'transition-colors group-hover:bg-black/25',
                    )}
                >
                    <span className="flex size-12 items-center justify-center rounded-full bg-black/60 text-white">
                        <Play className="size-5 translate-x-0.5 fill-current" />
                    </span>
                </span>
                {duration && (
                    <span
                        data-testid="guide-video-duration"
                        className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium tabular-nums text-white"
                    >
                        {duration}
                    </span>
                )}
            </span>
            <span className="flex flex-1 flex-col gap-1 p-3">
                <span className="font-medium leading-snug">{video.title}</span>
                {video.description && (
                    <span className="line-clamp-3 text-sm text-muted-foreground">
                        {video.description}
                    </span>
                )}
            </span>
        </button>
    )
}
